import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Response } from '../api/typegen/client/get';
import { useErrorManager } from '../InternalLibrary';
import axiosInstance from '../utils/axiosInstance';
import isLogged from '../utils/isLogged';

type Props = {
  children: ReactNode;
};

type ClientContextType = {
  client: Response | undefined | null;
  fetching: boolean;
  fetchClient: () => Promise<Response | undefined>;
};

const ClientContext = createContext<ClientContextType>({} as ClientContextType);

const useClient = (): ClientContextType => useContext(ClientContext);

const ClientProvider = ({ children }: Props): JSX.Element => {
  const [client, setClient] = useState<Response | undefined | null>();
  const [fetching, setFetching] = useState(false);
  const { setError } = useErrorManager();

  const fetchClient = useCallback(async (): Promise<Response> => {
    try {
      setFetching(true);
      const { data: fetchedClient } = await axiosInstance.get('/client');
      setClient(fetchedClient);
      return fetchedClient;
    } catch (e) {
      setClient(null);
      setError();
      throw e;
    } finally {
      setFetching(false);
    }
  }, [setError]);

  useEffect(() => {
    if (client === undefined && isLogged()) fetchClient();
  }, []);

  const clientContextValue = useMemo(
    () => ({
      client,
      fetchClient,
      fetching,
    }),
    [client, fetchClient, fetching],
  );

  return (
    <ClientContext.Provider value={clientContextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export { useClient as default, ClientProvider };
